import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { consumeReturnTo } from "@/lib/authReturn";

/**
 * Emergent OAuth callback landing.
 * URL shape after Google auth: /auth/callback#session_id=<...>
 * Exchange the session_id server-side (backend calls Emergent), receive
 * an httpOnly session_token cookie, then navigate to /app/dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const hash = location.hash || window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      const session_id = match ? decodeURIComponent(match[1]) : null;

      if (!session_id) {
        setError("Missing session id in callback URL.");
        return;
      }

      try {
        await api.post("/auth/emergent/session", { session_id });
        // Clean the fragment
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
        await checkAuth();
        navigate(consumeReturnTo("/app/dashboard"), { replace: true });
      } catch (e) {
        setError(formatApiError(e));
      }
    };
    run();
  }, [location.hash, navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-matte flex items-center justify-center px-6">
      <div className="max-w-md w-full border border-white/10 bg-matte-card p-10 text-center">
        <div className="mx-auto h-2 w-2 rounded-full bg-gold animate-pulse mb-6" />
        <div className="font-display uppercase tracking-[0.35em] text-[11px] text-gold">
          Signing you in
        </div>
        <div className="mt-4 text-white font-display uppercase tracking-wider text-lg">
          Verifying your Google session…
        </div>
        {error && (
          <div className="mt-6 text-sm text-red-300 border border-red-500/40 bg-red-500/10 px-3 py-2">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
